import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon, ArrowLeft, Loader2, ShieldOff, ShieldCheck,
  Unlock, UserCog, ChevronLeft, ChevronRight, ExternalLink, FileDown, Receipt,
} from 'lucide-react';
import {
  fetchAdminDrivers, fetchAdminDriver, updateAdminDriver, clearPaymentLockout,
  fetchAdminGroups, driverInvoicesCsvPath, driverReportPdfPath, fetchDriverSessions,
  driverSessionReceiptPath, driverSessionReceiptFallbackName,
  type DriverRow, type DriverDetailView, type DriverState, type DriverStateFilter,
  type DriverActivityFilter, type DriverJoinedFilter, type DriverSessionRow,
  type DriverSessionStatusFilter,
  type GroupRow,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import formStyles from '@/components/ui/Form.module.css';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { navigateInApp } from '@/components/sessions/DriverCell';
import {
  driverProfilePath, driverSessionsFrom, sessionDetailPath, sessionStatusLabel,
  type DriverSessionWindow,
} from '@/lib/sessionDriver';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthedDownload } from '@/hooks/useAuthedDownload';
import { useIsMobile } from '@/hooks/useViewport';
import { sessionOffersReceipt } from '@/lib/sessionReceipt';
import styles from './DriversPage.module.css';
import { Switch } from '@/components/ui/Switch';

/**
 * /drivers — admin surface for end-user driver accounts in
 * the caller's tenant.
 *
 * Distinct from /users (admin-tier teammates from Phase 2.7);
 * this page shows User docs with `role === 'driver'`. Read-
 * mostly: list, search, filter by state, view detail with
 * gamification stats + recent session list, plus two
 * mutating actions:
 *   1. Deactivate / reactivate via PATCH /admin/drivers/:id
 *   2. Clear payment lockout via the existing
 *      POST /admin/drivers/:id/clear-payment-lockout endpoint
 *      from the locked-out-drivers surface
 *
 * NOT a place to invite drivers (drivers self-register via
 * the PWA), edit phone numbers (PWA-only), or move tenants
 * (out of scope).
 */

const STATE_LABELS: Record<DriverState, string> = {
  active: 'Active',
  locked_out: 'Locked out',
  deactivated: 'Deactivated',
};

const STATE_CLASSES: Record<DriverState, string> = {
  active: styles.stateActive!,
  locked_out: styles.stateLockedOut!,
  deactivated: styles.stateDeactivated!,
};

function formatCents(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 })
    .format(cents / 100);
}

/** Each wallet in its own currency ("$12.00 · CA$5.00"); one wallet per
 *  currency since 2026-10-25. An older Api sends the USD balance only. */
function walletBalances(row: Pick<DriverRow, 'wallet_balance_cents' | 'wallets'>): Array<{ currency: string; text: string }> {
  const list = row.wallets && row.wallets.length > 0 ? row.wallets : [{ currency: 'USD', balanceCents: row.wallet_balance_cents }];
  return list.map((w) => ({ currency: w.currency, text: formatCents(w.balanceCents, w.currency) }));
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return 'just now';
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}

interface DriversPageProps {
  /** /drivers/:id (App.tsx): the driver profile, by its own address so a
   *  session row can link to it (item 9, 2026-10-23). Absent on /drivers. */
  driverId?: string | null;
  onNavigate?: (path: string) => void;
}

export function DriversPage({ driverId = null, onNavigate }: DriversPageProps = {}) {
  // The profile records its own recents entry, under the driver's name.
  useRecentRoute(driverId ? '' : 'Drivers');
  const go = onNavigate ?? navigateInApp;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<DriverStateFilter>('all');
  const [groupFilter, setGroupFilter] = useState<string>(''); // '' = all groups
  // Activity filter — anchored on charging activity (last_session_date),
  // not app presence (last_login). See lib/api's DriverActivityFilter
  // doc for the semantic rationale.
  const [activityFilter, setActivityFilter] = useState<DriverActivityFilter>('all');
  // Joined filter — Mongoose created_at on User.
  const [joinedFilter, setJoinedFilter] = useState<DriverJoinedFilter>('all');
  const [groupOptions, setGroupOptions] = useState<GroupRow[]>([]);
  const [page, setPage] = useState(1);

  // Load the group list once on mount for the filter pulldown.
  // Groups are tenant-scoped + small (a tenant rarely runs more
  // than dozens), so a single fetch is fine — no pagination,
  // no refetch on every driver-list reload.
  useEffect(() => {
    void fetchAdminGroups()
      .then(setGroupOptions)
      .catch(() => setGroupOptions([])); // soft-fail: the rest of the page still works
  }, []);

  // Search is debounced 250ms so each keystroke doesn't fan out to the
  // server.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(id);
  }, [search]);

  // The list, live (Warren, 28 Oct 2026): session, payout and points
  // events refetch it through LiveUpdatesProvider ('admin-drivers' in
  // LIVE_QUERY_KEYS), so the cards' numbers move as drivers charge. Page
  // and filters are in the key; keepPreviousData so paging does not blank
  // the table. staleTime 0 keeps the old reload on the way back from a
  // profile (driverId clears): an edit there shows in the list. The
  // profile is showing: the list is not, so it does not load.
  const listQ = useQuery({
    queryKey: ['admin-drivers', {
      page, search: debouncedSearch, stateFilter, groupFilter, activityFilter, joinedFilter,
    }],
    queryFn: () => fetchAdminDrivers({
      page,
      search: debouncedSearch || undefined,
      state: stateFilter,
      groupId: groupFilter || undefined,
      activity: activityFilter,
      joined: joinedFilter,
    }),
    enabled: !driverId,
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const rows: DriverRow[] = useMemo(() => listQ.data?.data ?? [], [listQ.data]);
  const loading = listQ.isPending;
  const error = listQ.error
    ? (listQ.error instanceof Error ? listQ.error.message : 'Failed to load drivers')
    : '';
  const totalPages = listQ.data?.meta.totalPages ?? 1;
  const total = listQ.data?.meta.total ?? 0;

  // useMemo MUST sit above any early return — rules-of-hooks
  // require every render to call hooks in the same order. The
  // earlier shape called this AFTER the `if (selectedId)` early
  // return, which crashed the detail-view render path with
  // "rendered fewer hooks than expected" the moment a user
  // clicked a row. This computation is cheap (filters a bounded
  // array) so unconditionally running it on the detail path is
  // wasted work but not user-visible.
  const summaryCounts = useMemo(() => {
    const active = rows.filter((r) => r.state === 'active').length;
    const lockedOut = rows.filter((r) => r.state === 'locked_out').length;
    const deactivated = rows.filter((r) => r.state === 'deactivated').length;
    return { active, lockedOut, deactivated };
  }, [rows]);

  if (driverId) {
    return (
      <DriverDetail
        key={driverId}
        driverId={driverId}
        onBack={() => go('/drivers')}
        onNavigate={go}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><UsersIcon size={18} /></div>
          <div>
            <h1 className={styles.title}>Drivers</h1>
            <p className={styles.subtitle}>
              {total} driver{total === 1 ? '' : 's'}
              {rows.length > 0 && (
                <>
                  {' · '}
                  {summaryCounts.active} active
                  {summaryCounts.lockedOut > 0 && ` · ${summaryCounts.lockedOut} locked out`}
                  {summaryCounts.deactivated > 0 && ` · ${summaryCounts.deactivated} deactivated`}
                  {' (this page)'}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        <input
          className={styles.search}
          type="text"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className={styles.select}
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value as DriverStateFilter); setPage(1); }}
          aria-label="Filter by state"
        >
          <option value="all">All states</option>
          <option value="active">Active</option>
          <option value="locked_out">Locked out</option>
          <option value="deactivated">Deactivated</option>
        </select>
        {/* Group filter pulldown — server-side filter via the
            ?group_id query param. Only renders when at least one
            group exists; tenants without groups never see a
            filter that does nothing. */}
        {groupOptions.length > 0 && (
          <select
            className={styles.select}
            value={groupFilter}
            onChange={(e) => { setGroupFilter(e.target.value); setPage(1); }}
            aria-label="Filter by customer group"
          >
            <option value="">All groups</option>
            {groupOptions.map((g) => (
              <option key={g._id} value={g._id}>
                In group: {g.name}
              </option>
            ))}
          </select>
        )}
        {/* Activity filter — charging activity windows. Anchored
            on last_session_date (not last_login) so "Active last
            7d" reflects driver who actually CHARGED in that
            window, not just opened the app. "Inactive 30+ days"
            includes drivers who never charged at all (last
            session = null counts as "very inactive"); "Never
            charged" surfaces only the registered-but-no-sessions
            cohort if operators want to chase activation. */}
        <select
          className={styles.select}
          value={activityFilter}
          onChange={(e) => { setActivityFilter(e.target.value as DriverActivityFilter); setPage(1); }}
          aria-label="Filter by activity"
        >
          <option value="all">All activity</option>
          <option value="last_7d">Active last 7 days</option>
          <option value="last_30d">Active last 30 days</option>
          <option value="last_90d">Active last 90 days</option>
          <option value="inactive_30d">Inactive 30+ days</option>
          <option value="never">Never charged</option>
        </select>
        {/* Joined filter — when the User doc was created
            (Mongoose created_at). Independent dimension from
            Activity; "joined this week + active last 7d"
            answers a different operational question
            ("activation funnel converting?") than "joined ever
            + active last 7d" ("currently engaged?"). */}
        <select
          className={styles.select}
          value={joinedFilter}
          onChange={(e) => { setJoinedFilter(e.target.value as DriverJoinedFilter); setPage(1); }}
          aria-label="Filter by joined date"
        >
          <option value="all">Joined any time</option>
          <option value="last_7d">Joined last 7 days</option>
          <option value="last_30d">Joined last 30 days</option>
          <option value="last_90d">Joined last 90 days</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && rows.length === 0 ? (
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>
          {search
            || stateFilter !== 'all'
            || groupFilter
            || activityFilter !== 'all'
            || joinedFilter !== 'all'
            ? 'No drivers match those filters.'
            : 'No drivers in this tenant yet.'}
        </div>
      ) : (
        <>
          <div className={styles.list}>
            {rows.map((r) => (
              <button
                key={r._id}
                type="button"
                className={styles.row}
                onClick={() => go(driverProfilePath(r._id))}
              >
                <div className={styles.rowMain}>
                  <span className={styles.rowName}>{r.name}</span>
                  <span className={styles.rowEmail}>{r.email}</span>
                </div>
                <div className={styles.rowSecondary}>
                  <span>
                    <span className={styles.rowSecondaryStrong}>{r.points.toFixed(0)}</span> pts
                    {' · '}
                    <span className={styles.rowSecondaryStrong}>{r.total_sessions}</span> sessions
                  </span>
                  <span>
                    Wallet <span className={styles.rowSecondaryStrong}>
                      {walletBalances(r).map((w) => w.text).join(' · ')}
                    </span>
                  </span>
                </div>
                <span className={`${styles.stateBadge} ${STATE_CLASSES[r.state]}`}>
                  {STATE_LABELS[r.state]}
                </span>
                <span className={styles.rowAside}>
                  Last seen {formatRelative(r.last_seen_at ?? r.last_session_date ?? r.last_login)}
                </span>
              </button>
            ))}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pagBtn}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft size={13} /> Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                type="button"
                className={styles.pagBtn}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next <ChevronRight size={13} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Detail view ─────────────────────────────────────────────────────

/**
 * The profile's session list (item 9, 2026-10-23): every session this
 * driver started, failed and pending included, newest first, a page at a
 * time from GET /admin/drivers/:id/sessions, each row opening the session.
 * The filters run server-side, so they reach the whole history, not only
 * the last 20.
 *
 * The status buckets are the ones this profile always offered, with the
 * failed one named for what it is: completed is every way a session ends
 * with energy delivered (COMPLETED_STATUSES, the set six other surfaces
 * use), failed is a start that never ran, in progress is pending or live.
 */
const COMPLETED_STATUSES = new Set<string>([
  'completed', 'stopped_by_user', 'stopped_by_system',
]);
const DRIVER_SESSIONS_PAGE_SIZE = 20;

interface DriverDetailProps {
  driverId: string;
  onBack: () => void;
  onNavigate: (path: string) => void;
}

function DriverDetail({ driverId, onBack, onNavigate }: DriverDetailProps) {
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // The session list's page and filters (server-side).
  const [sessionsPage, setSessionsPage] = useState(1);
  const [dateFilter, setDateFilter] = useState<DriverSessionWindow>('all');
  const [statusFilter, setStatusFilter] = useState<DriverSessionStatusFilter>('all');
  const [locationFilter, setLocationFilter] = useState<string>(''); // '' = all locations
  // The profile cards, live (Warren, 28 Oct 2026): the driver's sessions,
  // payouts and points refetch it through LiveUpdatesProvider
  // ('admin-driver' in LIVE_QUERY_KEYS), so Sessions, Energy, Spend,
  // Points and Last seen move while it is open.
  const viewQ = useQuery({
    queryKey: ['admin-driver', driverId],
    queryFn: () => fetchAdminDriver(driverId),
    staleTime: 0,
  });
  const view: DriverDetailView | null = viewQ.data ?? null;
  const loading = viewQ.isPending;
  const error = actionError
    || (viewQ.error ? (viewQ.error instanceof Error ? viewQ.error.message : 'Failed to load driver') : '');
  useRecentRoute(view ? `${view.name} (Driver)` : '');

  // After an edit or a lockout clear: this profile, and the list it
  // returns to (user writes are not live events).
  const load = async (): Promise<void> => {
    setActionError('');
    await Promise.all([
      viewQ.refetch(),
      queryClient.invalidateQueries({ queryKey: ['admin-drivers'] }),
    ]);
  };

  // A page of sessions. Page and filters are in the key; keepPreviousData
  // so paging does not blank the table. The date window becomes ?from at
  // fetch time, so the key is stable between renders. Above the early
  // returns below (rules-of-hooks).
  const sessionsQ = useQuery({
    queryKey: ['driver-sessions', driverId, { page: sessionsPage, dateFilter, statusFilter, locationFilter }],
    queryFn: () => fetchDriverSessions(driverId, {
      page: sessionsPage,
      limit: DRIVER_SESSIONS_PAGE_SIZE,
      status: statusFilter,
      from: driverSessionsFrom(dateFilter),
      locationId: locationFilter || undefined,
    }),
    placeholderData: keepPreviousData,
  });
  const sessionRows = sessionsQ.data?.data ?? [];
  // Export CSV, Download PDF and each row's Receipt: fetched with the
  // signed-in token and saved (a plain link reached the Api without it and
  // got a 401). One busy key per button; the failure reason shows above
  // the session list.
  const downloads = useAuthedDownload();
  const isMobile = useIsMobile();
  const sessionsTotal = sessionsQ.data?.meta.total ?? 0;
  const sessionsPages = Math.max(1, sessionsQ.data?.meta.totalPages ?? 1);
  const sessionLocations = sessionsQ.data?.meta.locations ?? [];

  const filtersActive =
    dateFilter !== 'all' || statusFilter !== 'all' || locationFilter !== '';

  const sessionColumns: Column<DriverSessionRow>[] = [
    {
      key: 'started',
      header: 'Started',
      mobile: 'primary',
      cell: (s) => (s.started_at ? new Date(s.started_at).toLocaleString() : 'Not recorded'),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'secondary',
      cell: (s) => (
        <span className={`${styles.sessionStatus} ${styles[`sessionStatus_${s.status}`] ?? ''}`}>
          {sessionStatusLabel(s.status)}
        </span>
      ),
    },
    {
      key: 'where',
      header: 'Location',
      mobile: 'body',
      cell: (s) => (
        <span className={styles.sessionWhere}>
          <span className={styles.sessionMain}>{s.location_name ?? 'Not recorded'}</span>
          {s.charger_identity && <span>{s.charger_identity}</span>}
        </span>
      ),
    },
    {
      key: 'energy',
      header: 'Energy',
      mobile: 'body',
      align: 'right',
      cell: (s) => <span className={styles.sessionStat}>{s.energy_kwh.toFixed(2)} kWh</span>,
    },
    {
      key: 'cost',
      header: 'Cost',
      mobile: 'body',
      align: 'right',
      // Cost, then the row's receipt buttons (receiptButtons below). On
      // mobile the buttons sit under the card instead (mobileRowActions):
      // the card is a button and cannot hold another.
      cell: (s) => (
        <span className={styles.sessionLastCell}>
          <span className={styles.sessionStat}>{formatCents(s.cost_total_cents, s.currency)}</span>
          {!isMobile && receiptButtons(s)}
        </span>
      ),
    },
  ];

  // The EVI Receipt PDF on every finished session and on a failed one that
  // really ran (Warren, 29 Oct 2026: support sends one session's receipt in
  // one click; lib/sessionReceipt.ts), and the Stripe-hosted receipt on a
  // completed session that has one (the PWA SessionDetailPage's "View
  // Invoice" rule). Both buttons stop the row's click, which opens the
  // session. Null when the row has neither.
  function receiptButtons(s: DriverSessionRow) {
    const offersReceipt = sessionOffersReceipt(s);
    const stripeReceipt = COMPLETED_STATUSES.has(s.status) && s.stripe_receipt_url ? s.stripe_receipt_url : null;
    if (!offersReceipt && !stripeReceipt) return null;
    const key = `receipt:${s._id}`;
    return (
      <>
        {offersReceipt && (
          <button
            type="button"
            className={styles.receiptDownloadBtn}
            aria-label="Download receipt PDF"
            title="Download receipt PDF"
            disabled={downloads.isBusy(key)}
            aria-busy={downloads.isBusy(key)}
            onClick={(e) => {
              e.stopPropagation();
              void downloads.download(
                key,
                driverSessionReceiptPath(driverId, s._id),
                driverSessionReceiptFallbackName(s._id, s.started_at),
              );
            }}
          >
            {downloads.isBusy(key)
              ? <Loader2 size={12} className={styles.spin} />
              : <Receipt size={12} />}
            Receipt
          </button>
        )}
        {stripeReceipt && (
          <button
            type="button"
            className={styles.receiptBtn}
            aria-label="View Stripe receipt"
            title="View Stripe receipt"
            onClick={(e) => {
              e.stopPropagation();
              window.open(stripeReceipt, '_blank', 'noopener,noreferrer');
            }}
          >
            <ExternalLink size={12} />
          </button>
        )}
      </>
    );
  }

  const handleClearLockout = async (): Promise<void> => {
    if (!view) return;
    if (!confirm(`Clear payment lockout for ${view.name}? They'll be able to attempt card payments immediately. The 30-day timer will not resume.`)) return;
    setBusy(true);
    try {
      await clearPaymentLockout(driverId);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to clear lockout');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !view) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Drivers
        </button>
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading…
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={onBack}>
          <ArrowLeft size={16} /> Back to Drivers
        </button>
        <div className={styles.error}>{error || 'Driver not found'}</div>
      </div>
    );
  }

  const stateLabel = STATE_LABELS[view.state];
  const stateClass = STATE_CLASSES[view.state];

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={onBack}>
        <ArrowLeft size={16} /> Back to Drivers
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><UsersIcon size={18} /></div>
          <div>
            <h1 className={styles.title}>{view.name}</h1>
            <p className={styles.subtitle}>
              {view.email}
              {view.current_rank && <> · {view.current_rank}</>}
            </p>
          </div>
        </div>
        <div className={styles.detailActions}>
          <span className={`${styles.stateBadge} ${stateClass}`}>{stateLabel}</span>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => setEditOpen(true)}
            disabled={busy}
          >
            <UserCog size={14} /> Edit
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* ── Group memberships ─────────────────────────────────────── */}
      {view.group_memberships.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--color-tx-m)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Member of:
          </span>
          {view.group_memberships.map((g) => (
            <span
              key={g._id}
              className={styles.stateBadge}
              style={{
                background: 'var(--color-sf-alt)',
                color: 'var(--color-tx)',
                borderColor: 'var(--color-brd)',
                textTransform: 'none',
                letterSpacing: 0,
                fontWeight: 600,
              }}
            >
              {g.name}
            </span>
          ))}
        </div>
      )}

      {/* ── Lockout card (only when locked out) ──────────────────── */}
      {view.state === 'locked_out' && view.payment_lockout_until && (
        <div className={styles.lockoutCard}>
          <div>
            <div className={styles.lockoutInfo}>
              <strong>Payment lockout active</strong> · {view.failed_charge_count} consecutive failed charges
            </div>
            <div className={`${styles.lockoutInfo} ${styles.lockoutInfoMute}`}>
              Until {new Date(view.payment_lockout_until).toLocaleString()}
            </div>
          </div>
          <button
            type="button"
            className={styles.dangerBtn}
            onClick={() => void handleClearLockout()}
            disabled={busy}
          >
            {busy ? <><Loader2 size={13} className={styles.spin} /> Clearing…</> : <><Unlock size={13} /> Clear lockout</>}
          </button>
        </div>
      )}

      {/* ── Stats grid ───────────────────────────────────────────── */}
      <h2 className={styles.sectionTitle}>Overview</h2>
      <div className={styles.statGrid}>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Sessions</p>
          <div className={styles.statValue}>{view.total_sessions}</div>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Energy</p>
          <div className={styles.statValue}>
            {view.total_kwh.toFixed(1)}
            <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 4 }}>kWh</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Lifetime spend</p>
          {(view.spend && view.spend.length > 0 ? view.spend : [{ currency: 'USD', cents: view.total_spending_cents }]).map((s) => (
            <div key={s.currency} className={styles.statValue} data-spend-currency={s.currency}>
              {formatCents(s.cents, s.currency)}
              {(view.spend?.length ?? 0) > 1 && <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 4 }}>{s.currency}</span>}
            </div>
          ))}
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>{walletBalances(view).length > 1 ? 'Wallets' : 'Wallet'}</p>
          {walletBalances(view).map((w) => (
            <div key={w.currency} className={styles.statValue} data-wallet-currency={w.currency}>
              {w.text}
              <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 4 }}>{w.currency}</span>
            </div>
          ))}
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Points</p>
          <div className={styles.statValue}>{view.points.toFixed(0)}</div>
          {(view.points_lifetime != null || view.current_rank) && (
            <p className={styles.statSub}>
              {view.points_lifetime != null && `${view.points_lifetime.toFixed(0)} lifetime`}
              {view.points_lifetime != null && view.current_rank && ' · '}
              {view.current_rank}
            </p>
          )}
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Streak</p>
          <div className={styles.statValue}>{view.current_streak_days}<span style={{ fontSize: 13, fontWeight: 500, marginLeft: 4 }}>day</span></div>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Last session</p>
          <div className={styles.statValue} style={{ fontSize: 16 }}>{formatRelative(view.last_session_date)}</div>
        </div>
        <div className={styles.statCard}>
          <p className={styles.statLabel}>Last seen</p>
          <div className={styles.statValue} style={{ fontSize: 16 }}>{formatRelative(view.last_seen_at ?? view.last_login)}</div>
        </div>
      </div>

      {/* Sessions: every one this driver started, a page at a time, each
          row opening the session (item 9, 2026-10-23). */}
      <div className={styles.sectionHeaderRow}>
        <h2 className={styles.sectionTitle} style={{ margin: 0 }}>
          Sessions
          {sessionsQ.data && (
            <span style={{ fontWeight: 400, color: 'var(--color-tx-m)', marginLeft: 8 }}>
              {/* While a new page or filter loads, the rows on screen are
                  the previous ones: say so rather than label them. */}
              {sessionsQ.isPlaceholderData
                ? '(updating)'
                : `(${sessionsTotal.toLocaleString()}${filtersActive ? ' matching' : ''})`}
            </span>
          )}
        </h2>
        {/* Export options: two distinct buttons rather than a single
            dropdown. "Export" is raw data (CSV); "Download" is a finished
            document (PDF). Both cover the driver's full session history
            (10000-row server cap), not just this page. Buttons, not
            links: the file is fetched with the signed-in token. */}
        <span className={styles.exportActions}>
          <button
            type="button"
            className={styles.exportBtn}
            disabled={downloads.isBusy('csv')}
            aria-busy={downloads.isBusy('csv')}
            onClick={() => void downloads.download('csv', driverInvoicesCsvPath(view._id), `invoices-driver-${view._id}.csv`)}
          >
            {downloads.isBusy('csv') ? <Loader2 size={12} className={styles.spin} /> : <ExternalLink size={12} />} Export CSV
          </button>
          <button
            type="button"
            className={styles.exportBtn}
            disabled={downloads.isBusy('pdf')}
            aria-busy={downloads.isBusy('pdf')}
            onClick={() => void downloads.download('pdf', driverReportPdfPath(view._id), `report-driver-${view._id}.pdf`)}
          >
            {downloads.isBusy('pdf') ? <Loader2 size={12} className={styles.spin} /> : <FileDown size={12} />} Download PDF
          </button>
        </span>
      </div>
      {downloads.error && (
        <div className={`${styles.error} ${styles.downloadError}`} role="alert">{downloads.error}</div>
      )}
      <div className={styles.filterRow}>
        <select
          className={styles.select}
          value={dateFilter}
          onChange={(e) => { setDateFilter(e.target.value as DriverSessionWindow); setSessionsPage(1); }}
          aria-label="Filter sessions by date"
        >
          <option value="all">Any date</option>
          <option value="last_7d">Last 7 days</option>
          <option value="last_30d">Last 30 days</option>
          <option value="last_90d">Last 90 days</option>
        </select>
        <select
          className={styles.select}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as DriverSessionStatusFilter); setSessionsPage(1); }}
          aria-label="Filter sessions by status"
        >
          <option value="all">Any status</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="in_progress">In progress</option>
        </select>
        {/* Only when the driver charged at two or more sites. */}
        {sessionLocations.length > 1 && (
          <select
            className={styles.select}
            value={locationFilter}
            onChange={(e) => { setLocationFilter(e.target.value); setSessionsPage(1); }}
            aria-label="Filter sessions by location"
          >
            <option value="">All locations</option>
            {sessionLocations.map((l) => (
              <option key={l._id} value={l._id}>{l.name ?? 'Unnamed site'}</option>
            ))}
          </select>
        )}
      </div>
      {sessionsQ.error ? (
        <div className={styles.error}>
          {sessionsQ.error instanceof Error ? sessionsQ.error.message : 'Failed to load sessions'}
        </div>
      ) : sessionsQ.isLoading ? (
        <div className={styles.loading}>
          <Loader2 size={18} className={styles.spin} /> Loading sessions
        </div>
      ) : (
        <div style={{ opacity: sessionsQ.isPlaceholderData ? 0.55 : 1 }} aria-busy={sessionsQ.isPlaceholderData}>
          <ResponsiveTable
            columns={sessionColumns}
            rows={sessionRows}
            rowKey={(s) => s._id}
            onRowClick={(s) => onNavigate(sessionDetailPath(s._id))}
            mobileRowActions={receiptButtons}
            emptyMessage={filtersActive ? 'No sessions match those filters.' : 'No sessions yet.'}
          />
        </div>
      )}
      {sessionsPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => setSessionsPage((p) => Math.max(1, p - 1))}
            disabled={sessionsPage <= 1}
          >
            <ChevronLeft size={13} /> Prev
          </button>
          <span>Page {sessionsPage} of {sessionsPages}</span>
          <button
            type="button"
            className={styles.pagBtn}
            onClick={() => setSessionsPage((p) => Math.min(sessionsPages, p + 1))}
            disabled={sessionsPage >= sessionsPages}
          >
            Next <ChevronRight size={13} />
          </button>
        </div>
      )}

      {editOpen && (
        <DriverEditModal
          driver={view}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ── Edit modal ──────────────────────────────────────────────────────

interface DriverEditModalProps {
  driver: DriverDetailView;
  onClose: () => void;
  onSaved: () => void;
}

function DriverEditModal({ driver, onClose, onSaved }: DriverEditModalProps) {
  const [name, setName] = useState(driver.name);
  const [isActive, setIsActive] = useState(driver.is_active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setError('');
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    const body: { name?: string; is_active?: boolean } = {};
    if (name.trim() !== driver.name) body.name = name.trim();
    if (isActive !== driver.is_active) body.is_active = isActive;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateAdminDriver(driver._id, body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      setSaving(false);
    }
  };

  const willDeactivate = driver.is_active && !isActive;
  const willReactivate = !driver.is_active && isActive;

  return (
    <Modal isOpen title={`Edit ${driver.name}`} onClose={onClose} size="md">
      <div className={styles.modalBody}>
        <div className={formStyles.field}>
          <label className={formStyles.label}>Name</label>
          <input
            className={formStyles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            autoFocus
          />
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>Email</label>
          <input
            className={formStyles.input}
            type="email"
            value={driver.email}
            disabled
          />
          <p className={styles.modalHint}>
            Email isn&rsquo;t editable from admin. Drivers manage their
            own email through the PWA.
          </p>
        </div>

        <div className={formStyles.field}>
          <label className={formStyles.label}>Status</label>
          <div className={styles.fieldRow}>
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={saving}
              label="Active"
            />
            <span className={`${styles.stateBadge} ${STATE_CLASSES[driver.state]}`}>
              {STATE_LABELS[driver.state]}
            </span>
          </div>
          {willDeactivate && (
            <div className={styles.modalDangerNote}>
              <strong><ShieldOff size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Deactivating</strong> blocks this driver from logging into the
              PWA on every auth path (password, refresh, magic link). Their
              data, sessions, and wallet are preserved — flip Active back on
              at any time to restore access.
            </div>
          )}
          {willReactivate && (
            <p className={styles.modalHint}>
              <ShieldCheck size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Reactivating restores PWA login immediately. The driver
              keeps their existing wallet balance, points, and session
              history.
            </p>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={formStyles.btnSecondary}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={formStyles.btnPrimary}
            onClick={() => void submit()}
            disabled={saving}
          >
            {saving
              ? <><Loader2 size={13} className={styles.spin} /> Saving…</>
              : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
